import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ThumbsUp, ThumbsDown, Clock, Users, CheckCircle, Scale, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/contexts/LocaleContext";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useRateLimit, RATE_LIMIT_CONFIGS } from "@/hooks/useRateLimit";
import { getResolutionTimeRemaining } from "@/lib/date-utils";
interface PendingResolution {
  id: string;
  event_id: string;
  proposed_outcome: boolean;
  dispute_deadline: string | null;
  dispute_count: number;
}

interface VoteOnResolutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  resolution: PendingResolution | null;
  eventTitle: string;
  onVoteSubmitted: () => void;
}

interface VoteCounts {
  agrees: number;
  disputes: number;
  agreesWeight: number;
  disputesWeight: number;
}

const VoteOnResolutionModal = ({
  isOpen,
  onClose,
  resolution,
  eventTitle,
  onVoteSubmitted,
}: VoteOnResolutionModalProps) => {
  const { locale } = useLocale();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [voteCounts, setVoteCounts] = useState<VoteCounts>({ agrees: 0, disputes: 0, agreesWeight: 0, disputesWeight: 0 });
  const [userVote, setUserVote] = useState<boolean | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>("");
  const [userVoteWeight, setUserVoteWeight] = useState<number>(0);
  const { isLimited, executeWithRateLimit } = useRateLimit({ maxActions: 5, windowMs: 60000 });

  useEffect(() => {
    if (isOpen && resolution) {
      fetchVotes();
      updateTimeRemaining();
      const interval = setInterval(updateTimeRemaining, 60000); // Update every minute
      return () => clearInterval(interval);
    }
  }, [isOpen, resolution]);

  const fetchVotes = async () => {
    if (!resolution || !user) return;

    // Fetch vote counts with weights
    const { data: votes } = await supabase
      .from("resolution_votes")
      .select("agrees_with_outcome, vote_weight")
      .eq("resolution_id", resolution.id);

    if (votes) {
      const agrees = votes.filter(v => v.agrees_with_outcome).length;
      const disputes = votes.filter(v => !v.agrees_with_outcome).length;
      const agreesWeight = votes.filter(v => v.agrees_with_outcome).reduce((sum, v) => sum + Number(v.vote_weight || 0), 0);
      const disputesWeight = votes.filter(v => !v.agrees_with_outcome).reduce((sum, v) => sum + Number(v.vote_weight || 0), 0);
      setVoteCounts({ agrees, disputes, agreesWeight, disputesWeight });
    }

    // Fetch user's vote
    const { data: userVoteData } = await supabase
      .from("resolution_votes")
      .select("agrees_with_outcome")
      .eq("resolution_id", resolution.id)
      .eq("user_id", user.id)
      .maybeSingle();

    if (userVoteData) {
      setUserVote(userVoteData.agrees_with_outcome);
    } else {
      setUserVote(null);
    }

    // Fetch user's vote weight (their bid amount)
    const { data: weightData } = await supabase
      .rpc("get_user_vote_weight", { _resolution_id: resolution.id, _user_id: user.id });
    
    setUserVoteWeight(Number(weightData) || 0);
  };

  const updateTimeRemaining = () => {
    if (!resolution?.dispute_deadline) {
      setTimeRemaining("");
      return;
    }
    setTimeRemaining(getResolutionTimeRemaining(resolution.dispute_deadline, locale.language as "en" | "pt"));
  };

  const handleVote = async (agreesWithOutcome: boolean) => {
    if (!user || !resolution) return;

    // Votes are final - cannot change
    if (userVote !== null) return;

    // Check minimum bid requirement
    if (userVoteWeight < 10) {
      toast.error(locale.language === "pt" ? "Aposta mínima de 10 CallyCoins necessária para votar" : "Minimum 10 CallyCoins bid required to vote");
      return;
    }

    if (isLimited) {
      toast.error(locale.language === "pt" ? "Muitos votos. Aguarde um momento." : "Too many votes. Please wait.");
      return;
    }

    const result = await executeWithRateLimit(async () => {
      setIsLoading(true);

      try {
        // Insert new vote with weight (votes are final, no updates)
        const { error } = await supabase
          .from("resolution_votes")
          .insert({
            resolution_id: resolution.id,
            user_id: user.id,
            agrees_with_outcome: agreesWithOutcome,
            vote_weight: userVoteWeight,
          });

        if (error) throw error;

        // Update dispute count if this is a dispute
        if (!agreesWithOutcome) {
          await supabase
            .from("pending_resolutions")
            .update({ dispute_count: (resolution.dispute_count || 0) + 1 })
            .eq("id", resolution.id);
        }

        toast.success(
          locale.language === "pt"
            ? agreesWithOutcome ? "Voto registrado: Concordo" : "Voto registrado: Disputo"
            : agreesWithOutcome ? "Vote recorded: Agree" : "Vote recorded: Dispute"
        );

        setUserVote(agreesWithOutcome);
        onVoteSubmitted();
        fetchVotes();
        return true;
      } catch (error: any) {
        toast.error(error.message || "Error submitting vote");
        return false;
      } finally {
        setIsLoading(false);
      }
    });

    return result;
  };

  if (!resolution) return null;

  const totalVotes = voteCounts.agrees + voteCounts.disputes;
  const totalWeight = voteCounts.agreesWeight + voteCounts.disputesWeight;
  const agreePercent = totalWeight > 0 ? (voteCounts.agreesWeight / totalWeight) * 100 : 50;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <div className="glass-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-foreground">
                  {locale.language === "pt" ? "Votar na Resolução" : "Vote on Resolution"}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg hover:bg-muted transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                {eventTitle}
              </p>

              {/* Proposed Outcome */}
              <div className="mb-4 p-3 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground mb-1">
                  {locale.language === "pt" ? "Resultado proposto:" : "Proposed outcome:"}
                </p>
                <p className={`font-bold text-lg ${resolution.proposed_outcome ? "text-success" : "text-destructive"}`}>
                  {resolution.proposed_outcome
                    ? (locale.language === "pt" ? "SIM Ganhou" : "YES Won")
                    : (locale.language === "pt" ? "NÃO Ganhou" : "NO Won")}
                </p>
              </div>

              {/* Time Remaining */}
              {timeRemaining && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-primary/10 border border-primary/20 mb-4">
                  <Clock size={18} className="text-primary" />
                  <span className="text-sm text-primary">
                    {locale.language === "pt" ? "Auto-aprovação em:" : "Auto-approval in:"} {timeRemaining}
                  </span>
                </div>
              )}

              {/* User's Vote Weight */}
              {userVoteWeight > 0 && (
                <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/10 border border-primary/20 mb-4">
                  <Scale size={14} className="text-primary" />
                  <span className="text-sm text-primary">
                    {locale.language === "pt" ? "Seu peso de voto:" : "Your vote weight:"} {userVoteWeight.toLocaleString()} CallyCoins
                  </span>
                </div>
              )}

              {/* Vote Distribution (Weighted) */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="flex items-center gap-1">
                    <Users size={14} className="text-muted-foreground" />
                    {totalVotes} {locale.language === "pt" ? "votos" : "votes"} ({totalWeight.toLocaleString()} {locale.language === "pt" ? "peso" : "weight"})
                  </span>
                  <span className="text-muted-foreground">
                    {agreePercent.toFixed(0)}% {locale.language === "pt" ? "concordam" : "agree"}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden flex">
                  <div
                    className="h-full bg-success transition-all duration-500"
                    style={{ width: `${agreePercent}%` }}
                  />
                  <div
                    className="h-full bg-destructive transition-all duration-500"
                    style={{ width: `${100 - agreePercent}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-success">{voteCounts.agrees} ({voteCounts.agreesWeight.toLocaleString()})</span>
                  <span className="text-destructive">{voteCounts.disputes} ({voteCounts.disputesWeight.toLocaleString()})</span>
                </div>
              </div>

              {/* User's vote status */}
              {userVote !== null ? (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-success/10 border border-success/20 mb-4">
                  <CheckCircle size={18} className="text-success" />
                  <span className="text-sm text-success font-medium">
                    {locale.language === "pt" ? "Você votou:" : "You voted:"}{" "}
                    {userVote
                      ? (locale.language === "pt" ? "Concordo" : "Agree")
                      : (locale.language === "pt" ? "Disputo" : "Dispute")}
                  </span>
                </div>
              ) : userVoteWeight < 10 ? (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 mb-4">
                  <Ban size={18} className="text-destructive" />
                  <span className="text-sm text-destructive">
                    {locale.language === "pt" 
                      ? "Aposta mínima de 10 CallyCoins necessária para votar" 
                      : "Minimum 10 CallyCoins bid required to vote"}
                  </span>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="success"
                    onClick={() => handleVote(true)}
                    disabled={isLoading}
                    className="flex items-center gap-2"
                  >
                    <ThumbsUp size={18} />
                    {locale.language === "pt" ? "Concordo" : "Agree"}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => handleVote(false)}
                    disabled={isLoading}
                    className="flex items-center gap-2"
                  >
                    <ThumbsDown size={18} />
                    {locale.language === "pt" ? "Disputo" : "Dispute"}
                  </Button>
                </div>
              )}

              <Button
                variant="ghost"
                onClick={onClose}
                className="w-full mt-3"
              >
                {locale.language === "pt" ? "Fechar" : "Close"}
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default VoteOnResolutionModal;
